import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, adminReportsAPI, adminKbAPI, adminHelpFeedbackAPI, KB_STUDY_TYPES } from '../services/api';
import { endSession } from '../services/session';
import './AdminPanel.css';

function isoToday() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function isoNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

// ── Q&A report card ─────────────────────────────────────────────────────

function QAReportCard() {
  const [from, setFrom] = useState(isoNDaysAgo(30));
  const [to, setTo] = useState(isoToday());
  const [allUsers, setAllUsers] = useState(true);
  const [username, setUsername] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const lookupTimer = useRef(null);

  useEffect(() => {
    if (allUsers) {
      setSuggestions([]);
      setShowSuggestions(false);
      return undefined;
    }
    if (!username.trim()) {
      setSuggestions([]);
      return undefined;
    }
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(async () => {
      try {
        const matches = await adminReportsAPI.searchUsers(username.trim());
        setSuggestions(matches || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 200);
    return () => {
      if (lookupTimer.current) clearTimeout(lookupTimer.current);
    };
  }, [username, allUsers]);

  const handleExport = async (e) => {
    e.preventDefault();
    setError('');
    if (!allUsers && !username.trim()) {
      setError('Enter a username or switch to "All users".');
      return;
    }
    setDownloading(true);
    try {
      const params = { from, to };
      if (!allUsers) params.username = username.trim();
      await adminReportsAPI.download('qa', params);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Export failed.';
      setError(typeof detail === 'string' ? detail : 'Export failed.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <form className="report-card" onSubmit={handleExport}>
      <div className="report-card-header">
        <div className="report-card-icon">💬</div>
        <div>
          <h3 className="report-card-title">Questions &amp; Answers</h3>
          <p className="report-card-subtitle">
            Export all questions and answers, optionally filtered to one user.
          </p>
        </div>
      </div>

      <div className="report-form">
        <div className="form-row">
          <label className="form-field">
            <span className="form-label">From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              max={to}
              required
            />
          </label>
          <label className="form-field">
            <span className="form-label">To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              min={from}
              max={isoToday()}
              required
            />
          </label>
        </div>

        <div className="form-row">
          <div className="form-field form-field--full">
            <span className="form-label">Users</span>
            <div className="segmented">
              <button
                type="button"
                className={`segmented-option ${allUsers ? 'is-active' : ''}`}
                onClick={() => setAllUsers(true)}
              >
                All users
              </button>
              <button
                type="button"
                className={`segmented-option ${!allUsers ? 'is-active' : ''}`}
                onClick={() => setAllUsers(false)}
              >
                Specific user
              </button>
            </div>
          </div>
        </div>

        {!allUsers && (
          <div className="form-row">
            <div className="form-field form-field--full autocomplete-wrap">
              <span className="form-label">Username</span>
              <input
                type="text"
                placeholder="Start typing a username…"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                autoComplete="off"
              />
              {showSuggestions && suggestions.length > 0 && (
                <ul className="autocomplete-list">
                  {suggestions.map((s) => (
                    <li
                      key={s.username}
                      onMouseDown={(ev) => {
                        ev.preventDefault();
                        setUsername(s.username);
                        setShowSuggestions(false);
                      }}
                    >
                      <span className="autocomplete-name">{s.username}</span>
                      <span className="autocomplete-email">{s.email}</span>
                      {s.is_admin && <span className="autocomplete-admin">Admin</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {error && <div className="report-error">{error}</div>}

        <div className="form-actions">
          <button type="submit" className="report-submit" disabled={downloading}>
            {downloading ? 'Preparing…' : 'Export CSV'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ── Feedback report card ────────────────────────────────────────────────────

function FeedbackReportCard() {
  const [from, setFrom] = useState(isoNDaysAgo(30));
  const [to, setTo] = useState(isoToday());
  const [polarity, setPolarity] = useState('both');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const handleExport = async (e) => {
    e.preventDefault();
    setError('');
    setDownloading(true);
    try {
      await adminReportsAPI.download('feedback', { from, to, polarity });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Export failed.';
      setError(typeof detail === 'string' ? detail : 'Export failed.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <form className="report-card" onSubmit={handleExport}>
      <div className="report-card-header">
        <div className="report-card-icon">⭐</div>
        <div>
          <h3 className="report-card-title">Feedback</h3>
          <p className="report-card-subtitle">
            Export thumbs-up/down ratings and comments left on answers.
          </p>
        </div>
      </div>

      <div className="report-form">
        <div className="form-row">
          <label className="form-field">
            <span className="form-label">From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              max={to}
              required
            />
          </label>
          <label className="form-field">
            <span className="form-label">To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              min={from}
              max={isoToday()}
              required
            />
          </label>
        </div>

        <div className="form-row">
          <div className="form-field form-field--full">
            <span className="form-label">Polarity</span>
            <div className="segmented">
              <button
                type="button"
                className={`segmented-option ${polarity === 'positive' ? 'is-active' : ''}`}
                onClick={() => setPolarity('positive')}
              >
                <span aria-hidden="true">👍</span> Positive
              </button>
              <button
                type="button"
                className={`segmented-option ${polarity === 'negative' ? 'is-active' : ''}`}
                onClick={() => setPolarity('negative')}
              >
                <span aria-hidden="true">👎</span> Negative
              </button>
              <button
                type="button"
                className={`segmented-option ${polarity === 'both' ? 'is-active' : ''}`}
                onClick={() => setPolarity('both')}
              >
                Both
              </button>
            </div>
          </div>
        </div>

        {error && <div className="report-error">{error}</div>}

        <div className="form-actions">
          <button type="submit" className="report-submit" disabled={downloading}>
            {downloading ? 'Preparing…' : 'Export CSV'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ── Logins report card ──────────────────────────────────────────────────────

function LoginsReportCard() {
  const [from, setFrom] = useState(isoNDaysAgo(30));
  const [to, setTo] = useState(isoToday());
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const handleExport = async (e) => {
    e.preventDefault();
    setError('');
    setDownloading(true);
    try {
      await adminReportsAPI.download('logins', { from, to });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Export failed.';
      setError(typeof detail === 'string' ? detail : 'Export failed.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <form className="report-card" onSubmit={handleExport}>
      <div className="report-card-header">
        <div className="report-card-icon">🔐</div>
        <div>
          <h3 className="report-card-title">Logins</h3>
          <p className="report-card-subtitle">
            Per-user login counts by date plus a total for each user.
          </p>
        </div>
      </div>

      <div className="report-form">
        <div className="form-row">
          <label className="form-field">
            <span className="form-label">From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              max={to}
              required
            />
          </label>
          <label className="form-field">
            <span className="form-label">To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              min={from}
              max={isoToday()}
              required
            />
          </label>
        </div>

        {error && <div className="report-error">{error}</div>}

        <div className="form-actions">
          <button type="submit" className="report-submit" disabled={downloading}>
            {downloading ? 'Preparing…' : 'Export CSV'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ── CME Certificates report card ────────────────────────────────────────────

function CertificatesReportCard() {
  const [useDateFilter, setUseDateFilter] = useState(false);
  const [from, setFrom] = useState(isoNDaysAgo(30));
  const [to, setTo] = useState(isoToday());
  const [email, setEmail] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const handleExport = async (e) => {
    e.preventDefault();
    setError('');
    setDownloading(true);
    try {
      const params = {};
      if (useDateFilter) {
        params.from = from;
        params.to = to;
      }
      if (email.trim()) {
        params.email = email.trim();
      }
      await adminReportsAPI.download('certificates', params);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Export failed.';
      setError(typeof detail === 'string' ? detail : 'Export failed.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <form className="report-card" onSubmit={handleExport}>
      <div className="report-card-header">
        <div className="report-card-icon">🎓</div>
        <div>
          <h3 className="report-card-title">CME Certificates</h3>
          <p className="report-card-subtitle">
            Export issued certificates. Filters are optional — leave them empty
            to export the entire table.
          </p>
        </div>
      </div>

      <div className="report-form">
        <div className="form-row">
          <div className="form-field form-field--full">
            <span className="form-label">Date filter</span>
            <div className="segmented">
              <button
                type="button"
                className={`segmented-option ${!useDateFilter ? 'is-active' : ''}`}
                onClick={() => setUseDateFilter(false)}
              >
                All time
              </button>
              <button
                type="button"
                className={`segmented-option ${useDateFilter ? 'is-active' : ''}`}
                onClick={() => setUseDateFilter(true)}
              >
                Date range
              </button>
            </div>
          </div>
        </div>

        {useDateFilter && (
          <div className="form-row">
            <label className="form-field">
              <span className="form-label">From</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                max={to}
                required={useDateFilter}
              />
            </label>
            <label className="form-field">
              <span className="form-label">To</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                min={from}
                max={isoToday()}
                required={useDateFilter}
              />
            </label>
          </div>
        )}

        <div className="form-row">
          <label className="form-field form-field--full">
            <span className="form-label">User email (optional)</span>
            <input
              type="text"
              placeholder="e.g. jane.doe@telus.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
            />
          </label>
        </div>

        {error && <div className="report-error">{error}</div>}

        <div className="form-actions">
          <button type="submit" className="report-submit" disabled={downloading}>
            {downloading ? 'Preparing…' : 'Export CSV'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ── Help-feedback report card ───────────────────────────────────────────────

function HelpFeedbackReportCard() {
  const [useDateFilter, setUseDateFilter] = useState(false);
  const [from, setFrom] = useState(isoNDaysAgo(30));
  const [to, setTo] = useState(isoToday());
  const [statusFilter, setStatusFilter] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const handleExport = async (e) => {
    e.preventDefault();
    setError('');
    setDownloading(true);
    try {
      const params = {};
      if (useDateFilter) {
        params.from = from;
        params.to = to;
      }
      if (statusFilter) params.status = statusFilter;
      await adminReportsAPI.download('help-feedback', params);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Export failed.';
      setError(typeof detail === 'string' ? detail : 'Export failed.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <form className="report-card" onSubmit={handleExport}>
      <div className="report-card-header">
        <div className="report-card-icon">🆘</div>
        <div>
          <h3 className="report-card-title">Help Feedback</h3>
          <p className="report-card-subtitle">
            Export every Help-form submission — including triaged ones — with
            the current status and the admin who set it.
          </p>
        </div>
      </div>

      <div className="report-form">
        <div className="form-row">
          <div className="form-field form-field--full">
            <span className="form-label">Date filter</span>
            <div className="segmented">
              <button
                type="button"
                className={`segmented-option ${!useDateFilter ? 'is-active' : ''}`}
                onClick={() => setUseDateFilter(false)}
              >
                All time
              </button>
              <button
                type="button"
                className={`segmented-option ${useDateFilter ? 'is-active' : ''}`}
                onClick={() => setUseDateFilter(true)}
              >
                Date range
              </button>
            </div>
          </div>
        </div>

        {useDateFilter && (
          <div className="form-row">
            <label className="form-field">
              <span className="form-label">From</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                max={to}
                required={useDateFilter}
              />
            </label>
            <label className="form-field">
              <span className="form-label">To</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                min={from}
                max={isoToday()}
                required={useDateFilter}
              />
            </label>
          </div>
        )}

        <div className="form-row">
          <label className="form-field form-field--full">
            <span className="form-label">Status (optional)</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              {HELP_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        {error && <div className="report-error">{error}</div>}

        <div className="form-actions">
          <button type="submit" className="report-submit" disabled={downloading}>
            {downloading ? 'Preparing…' : 'Export CSV'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ── Knowledge Base upload card ──────────────────────────────────────────────

// Ordered list of stages we show in the checklist. Stages that never fire
// for a given upload (e.g. "classify" on Internal/External) stay greyed out.
const KB_STAGES = [
  { key: 'scan',       label: 'Virus scan' },
  { key: 'dedupe',     label: 'Duplicate check' },
  { key: 'extract',    label: 'Extract text from PDF' },
  { key: 'classify',   label: 'Classify document type' },
  { key: 'chunk',      label: 'Chunk text' },
  { key: 'embed',      label: 'Generate embeddings' },
  { key: 'upload_gcs', label: 'Upload PDF to storage' },
  { key: 'metadata',   label: 'Update metadata index' },
  { key: 'manifest',   label: 'Update ingestion manifest' },
  { key: 'upsert',     label: 'Write to vector store' },
];

function StageRow({ label, state, progress }) {
  let icon = '⏳';
  if (state === 'running' || state === 'progress') icon = '🔄';
  else if (state === 'done') icon = '✅';
  else if (state === 'error') icon = '❌';
  return (
    <li className={`kb-stage kb-stage--${state || 'pending'}`}>
      <span className="kb-stage-icon">{icon}</span>
      <span className="kb-stage-label">{label}</span>
      {progress && (
        <span className="kb-stage-progress">
          {progress.current} / {progress.total}
        </span>
      )}
    </li>
  );
}

function KBFileListCard({ refreshKey, onAfterDelete }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null); // filename | null
  const [deletingName, setDeletingName] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminKbAPI.listFiles();
      setFiles(data?.files || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Failed to load file list.';
      setError(typeof detail === 'string' ? detail : 'Failed to load file list.');
    } finally {
      setLoading(false);
    }
  };

  // Reload on mount, manual refresh, and after each successful upload.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleDeleteClick = (filename) => {
    setError('');
    setConfirmTarget(filename);
  };

  const handleCancelDelete = () => {
    setConfirmTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    const target = confirmTarget;
    setDeletingName(target);
    setConfirmTarget(null);
    setError('');
    try {
      await adminKbAPI.deleteFile(target);
      await load();
      if (onAfterDelete) onAfterDelete(target);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Delete failed.';
      setError(typeof detail === 'string' ? detail : 'Delete failed.');
    } finally {
      setDeletingName('');
    }
  };

  return (
    <div className="report-card kb-files-card">
      <div className="report-card-header">
        <div className="report-card-icon">📂</div>
        <div className="kb-files-header-text">
          <h3 className="report-card-title">Knowledge Base Files</h3>
          <p className="report-card-subtitle">
            Live list of files in the Knowledge Base
          </p>
        </div>
        <button
          type="button"
          className="kb-refresh-btn"
          onClick={load}
          disabled={loading}
          title="Refresh from GCS manifest"
          aria-label="Refresh file list"
        >
          {loading ? '⟳ …' : '⟳ Refresh'}
        </button>
      </div>

      {error && <div className="report-error">{error}</div>}

      <ul className="kb-file-list">
        {!loading && files.length === 0 && (
          <li className="kb-file-empty">No files in the knowledge base yet.</li>
        )}
        {files.map((f) => {
          const isDeleting = deletingName === f.filename;
          return (
            <li key={f.filename} className="kb-file-item">
              <div className="kb-file-info">
                <span className="kb-file-name" title={f.filename}>{f.filename}</span>
                {f.type && <span className="kb-file-type">{f.type}</span>}
              </div>
              <button
                type="button"
                className="kb-delete-btn"
                onClick={() => handleDeleteClick(f.filename)}
                disabled={isDeleting || !!deletingName}
                aria-label={`Delete ${f.filename}`}
                title="Delete from knowledge base"
              >
                {isDeleting ? '…' : '🗑'}
              </button>
            </li>
          );
        })}
      </ul>

      {confirmTarget && (
        <div className="kb-confirm-panel kb-delete-confirm">
          <h4>Delete this file?</h4>
          <p>
            <strong>{confirmTarget}</strong> will be removed from GCS, the
            metadata CSV, the ingestion manifest, and the vector namespace.
            This cannot be undone.
          </p>
          <div className="kb-confirm-actions">
            <button
              type="button"
              className="kb-cancel-btn"
              onClick={handleCancelDelete}
            >
              Cancel
            </button>
            <button
              type="button"
              className="kb-delete-confirm-btn"
              onClick={handleConfirmDelete}
            >
              Yes, delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function KBUploadCard({ onUploadComplete }) {
  // Form state
  const [file, setFile] = useState(null);
  const [type, setType] = useState('internal');           // 'internal'|'emerging'|'external'|'other'
  const [citation, setCitation] = useState('');
  const [canadian, setCanadian] = useState(false);
  const [highImpact, setHighImpact] = useState(false);

  // Stream state
  const [running, setRunning] = useState(false);
  const [stageState, setStageState] = useState({});       // { stageKey: 'running'|'progress'|'done' }
  const [stageProgress, setStageProgress] = useState({}); // { stageKey: {current,total} }
  const [pendingConfirm, setPendingConfirm] = useState(null); // { suggestedType, changeKind }
  const [confirmChoice, setConfirmChoice] = useState('');
  const [result, setResult] = useState(null);             // { changeKind, finalType, chunksWritten, sha256 }
  const [error, setError] = useState('');
  const controllerRef = useRef(null);

  const resetStream = () => {
    setStageState({});
    setStageProgress({});
    setPendingConfirm(null);
    setConfirmChoice('');
    setResult(null);
    setError('');
  };

  const handleEvent = (event) => {
    if (event.type === 'stage') {
      setStageState((s) => ({ ...s, [event.stage]: event.state === 'done' ? 'done' : 'running' }));
      if (event.state === 'progress' && typeof event.current === 'number') {
        setStageProgress((p) => ({
          ...p,
          [event.stage]: { current: event.current, total: event.total },
        }));
      } else if (event.state === 'done') {
        setStageProgress((p) => {
          const next = { ...p };
          delete next[event.stage];
          return next;
        });
      }
    } else if (event.type === 'needs_confirmation') {
      setPendingConfirm({
        suggestedType: event.suggested_type,
        changeKind: event.change_kind,
      });
      setConfirmChoice(event.suggested_type);
      setRunning(false);
    } else if (event.type === 'complete') {
      setResult({
        changeKind: event.change_kind,
        finalType: event.final_type,
        chunksWritten: event.chunks_written,
        sha256: event.sha256,
      });
      setRunning(false);
      if (onUploadComplete) onUploadComplete();
    } else if (event.type === 'error') {
      setError(event.message || 'Upload failed.');
      setRunning(false);
    }
  };

  const startUpload = (confirmedType) => {
    if (!file) {
      setError('Please choose a PDF file.');
      return;
    }
    if (!citation.trim()) {
      setError('Please enter a citation.');
      return;
    }
    resetStream();
    setRunning(true);
    controllerRef.current = adminKbAPI.upload(
      {
        file,
        type,
        citation: citation.trim(),
        canadianGuideline: canadian,
        highImpact,
        confirmedType,
      },
      {
        onEvent: handleEvent,
        onDone: () => setRunning(false),
        onError: (msg) => { setError(msg); setRunning(false); },
      },
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    startUpload(undefined);
  };

  const handleConfirm = () => {
    if (!confirmChoice) {
      setError('Please pick a category.');
      return;
    }
    startUpload(confirmChoice);
  };

  const handleCancel = () => {
    if (controllerRef.current) controllerRef.current.abort();
    setRunning(false);
    setPendingConfirm(null);
  };

  const handleReset = () => {
    setFile(null);
    setType('internal');
    setCitation('');
    setCanadian(false);
    setHighImpact(false);
    resetStream();
  };

  // Disable form while a stream is in flight OR while we're waiting on confirmation.
  const formLocked = running || !!pendingConfirm || !!result;

  return (
    <form className="report-card kb-card" onSubmit={handleSubmit}>
      <div className="report-card-header">
        <div className="report-card-icon">📚</div>
        <div>
          <h3 className="report-card-title">Upload PDF to Knowledge Base</h3>
          <p className="report-card-subtitle">
            One PDF at a time. The file goes through virus scan → dedupe → text
            extraction → embedding → indexing. Up to ~300 pages supported; a
            full document typically takes 1–3 minutes.
          </p>
        </div>
      </div>

      <div className="report-form">
        {/* File picker */}
        <div className="form-row">
          <label className="form-field form-field--full">
            <span className="form-label">PDF file</span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={formLocked}
              required
            />
            {file && (
              <span className="kb-file-meta">
                {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB
              </span>
            )}
          </label>
        </div>

        {/* Type */}
        <div className="form-row">
          <div className="form-field form-field--full">
            <span className="form-label">Document type</span>
            <div className="segmented">
              <button
                type="button"
                className={`segmented-option ${type === 'internal' ? 'is-active' : ''}`}
                onClick={() => setType('internal')}
                disabled={formLocked}
              >
                Internal guidance
              </button>
              <button
                type="button"
                className={`segmented-option ${type === 'emerging' ? 'is-active' : ''}`}
                onClick={() => setType('emerging')}
                disabled={formLocked}
              >
                THCC-prioritized emerging research
              </button>
              <button
                type="button"
                className={`segmented-option ${type === 'external' ? 'is-active' : ''}`}
                onClick={() => setType('external')}
                disabled={formLocked}
              >
                THCC-prioritized external guidance
              </button>
              <button
                type="button"
                className={`segmented-option ${type === 'other' ? 'is-active' : ''}`}
                onClick={() => setType('other')}
                disabled={formLocked}
              >
                THCC-prioritized other
              </button>
            </div>
          </div>
        </div>

        {/* Citation */}
        <div className="form-row">
          <label className="form-field form-field--full">
            <span className="form-label">Citation</span>
            <textarea
              rows={3}
              placeholder="Vancouver-style citation. If not applicable, use the filename — it must include the year of publication."
              value={citation}
              onChange={(e) => setCitation(e.target.value)}
              disabled={formLocked}
              maxLength={500}
              required
            />
          </label>
        </div>

        {/* Canadian guideline / High impact */}
        <div className="form-row">
          <div className="form-field">
            <span className="form-label">Canadian guideline?</span>
            <div className="segmented">
              <button
                type="button"
                className={`segmented-option ${canadian ? 'is-active' : ''}`}
                onClick={() => setCanadian(true)}
                disabled={formLocked}
              >
                Yes
              </button>
              <button
                type="button"
                className={`segmented-option ${!canadian ? 'is-active' : ''}`}
                onClick={() => setCanadian(false)}
                disabled={formLocked}
              >
                No
              </button>
            </div>
          </div>
          <div className="form-field">
            <span className="form-label">High impact?</span>
            <div className="segmented">
              <button
                type="button"
                className={`segmented-option ${highImpact ? 'is-active' : ''}`}
                onClick={() => setHighImpact(true)}
                disabled={formLocked}
              >
                Yes
              </button>
              <button
                type="button"
                className={`segmented-option ${!highImpact ? 'is-active' : ''}`}
                onClick={() => setHighImpact(false)}
                disabled={formLocked}
              >
                No
              </button>
            </div>
          </div>
        </div>

        {error && <div className="report-error">{error}</div>}

        <div className="form-actions">
          {!result && (
            <button
              type="submit"
              className="report-submit"
              disabled={formLocked || !file || !citation.trim()}
            >
              {running ? 'Uploading…' : 'Upload to Knowledge Base'}
            </button>
          )}
          {result && (
            <button type="button" className="report-submit" onClick={handleReset}>
              Upload another
            </button>
          )}
        </div>

        {/* Progress checklist (shown once an upload starts) */}
        {(running || pendingConfirm || result || Object.keys(stageState).length > 0) && (
          <ul className="kb-progress">
            {KB_STAGES.map((s) => (
              <StageRow
                key={s.key}
                label={s.label}
                state={stageState[s.key]}
                progress={stageProgress[s.key]}
              />
            ))}
          </ul>
        )}

        {/* Confirmation panel for 'other' uploads */}
        {pendingConfirm && (
          <div className="kb-confirm-panel">
            <h4>Confirm document category</h4>
            <p>
              The classifier suggests <strong>{pendingConfirm.suggestedType}</strong>.
              Accept this, or pick a different category from the list below.
              ({pendingConfirm.changeKind === 'updated'
                ? 'This is an updated version of an existing file.'
                : 'This is a new file.'})
            </p>
            <select
              className="kb-confirm-select"
              value={confirmChoice}
              onChange={(e) => setConfirmChoice(e.target.value)}
            >
              {KB_STUDY_TYPES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <div className="kb-confirm-actions">
              <button type="button" className="report-submit" onClick={handleConfirm}>
                Confirm &amp; ingest
              </button>
              <button type="button" className="kb-cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Success summary */}
        {result && (
          <div className="kb-success">
            <h4>✅ Upload complete</h4>
            <dl>
              <dt>Change</dt><dd>{result.changeKind === 'new' ? 'New file added' : 'Existing file updated'}</dd>
              <dt>Classified as</dt><dd>{result.finalType}</dd>
              <dt>Chunks written</dt><dd>{result.chunksWritten}</dd>
              <dt>Content hash</dt><dd className="kb-mono">{result.sha256.slice(0, 16)}…</dd>
            </dl>
          </div>
        )}
      </div>
    </form>
  );
}

function KBSection() {
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = () => setRefreshKey((k) => k + 1);
  return (
    <div className="kb-section-grid">
      <KBUploadCard onUploadComplete={bumpRefresh} />
      <KBFileListCard refreshKey={refreshKey} onAfterDelete={bumpRefresh} />
    </div>
  );
}

// ── User-manual PDF replace card ──────────────────────────────────────────

function UserManualUploadCard() {
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState('en');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const reset = () => {
    setFile(null);
    setResult(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    if (!file) {
      setError('Please choose a PDF file.');
      return;
    }
    setSubmitting(true);
    try {
      const data = await adminHelpFeedbackAPI.uploadUserManual({ file, language });
      setResult(data);
    } catch (err) {
      setError(err?.message || 'Upload failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="report-card" onSubmit={handleSubmit}>
      <div className="report-card-header">
        <div className="report-card-icon">📘</div>
        <div>
          <h3 className="report-card-title">Replace User Manual PDF</h3>
          <p className="report-card-subtitle">
            Upload a PDF to overwrite the current User Manual for the selected
            language. The new file becomes available immediately to the Help
            modal on the login page and inside the app.
          </p>
        </div>
      </div>

      <div className="report-form">
        <div className="form-row">
          <div className="form-field form-field--full">
            <span className="form-label">Language</span>
            <div className="segmented">
              <button
                type="button"
                className={`segmented-option ${language === 'en' ? 'is-active' : ''}`}
                onClick={() => setLanguage('en')}
                disabled={submitting}
              >
                English
              </button>
              <button
                type="button"
                className={`segmented-option ${language === 'fr' ? 'is-active' : ''}`}
                onClick={() => setLanguage('fr')}
                disabled={submitting}
              >
                French
              </button>
            </div>
          </div>
        </div>

        <div className="form-row">
          <label className="form-field form-field--full">
            <span className="form-label">PDF file</span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={submitting}
              required
            />
            {file && (
              <span className="kb-file-meta">
                {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB
              </span>
            )}
          </label>
        </div>

        {error && <div className="report-error">{error}</div>}

        <div className="form-actions">
          {!result && (
            <button
              type="submit"
              className="report-submit"
              disabled={submitting || !file}
            >
              {submitting ? 'Uploading…' : 'Replace User Manual'}
            </button>
          )}
          {result && (
            <button type="button" className="report-submit" onClick={reset}>
              Upload another
            </button>
          )}
        </div>

        {result && (
          <div className="kb-success">
            <h4>✅ Upload complete</h4>
            <dl>
              <dt>Language</dt><dd>{result.language === 'fr' ? 'French' : 'English'}</dd>
              <dt>Object path</dt><dd className="kb-mono">{result.object_path}</dd>
              <dt>Size</dt><dd>{(result.size / (1024 * 1024)).toFixed(2)} MB</dd>
            </dl>
          </div>
        )}
      </div>
    </form>
  );
}

// ── Admin manual PDF replace card ─────────────────────────────────────────

function AdminManualUploadCard() {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const reset = () => {
    setFile(null);
    setResult(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    if (!file) {
      setError('Please choose a PDF file.');
      return;
    }
    setSubmitting(true);
    try {
      const data = await adminHelpFeedbackAPI.uploadAdminManual({ file });
      setResult(data);
    } catch (err) {
      setError(err?.message || 'Upload failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="report-card" onSubmit={handleSubmit}>
      <div className="report-card-header">
        <div className="report-card-icon">📕</div>
        <div>
          <h3 className="report-card-title">Replace Admin Manual PDF</h3>
          <p className="report-card-subtitle">
            Upload a PDF to overwrite the current Admin Manual (English only).
            The new file is available immediately from the Admin Guide link in
            the header.
          </p>
        </div>
      </div>

      <div className="report-form">
        <div className="form-row">
          <label className="form-field form-field--full">
            <span className="form-label">PDF file</span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={submitting}
              required
            />
            {file && (
              <span className="kb-file-meta">
                {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB
              </span>
            )}
          </label>
        </div>

        {error && <div className="report-error">{error}</div>}

        <div className="form-actions">
          {!result && (
            <button
              type="submit"
              className="report-submit"
              disabled={submitting || !file}
            >
              {submitting ? 'Uploading…' : 'Replace Admin Manual'}
            </button>
          )}
          {result && (
            <button type="button" className="report-submit" onClick={reset}>
              Upload another
            </button>
          )}
        </div>

        {result && (
          <div className="kb-success">
            <h4>✅ Upload complete</h4>
            <dl>
              <dt>Object path</dt><dd className="kb-mono">{result.object_path}</dd>
              <dt>Size</dt><dd>{(result.size / (1024 * 1024)).toFixed(2)} MB</dd>
            </dl>
          </div>
        )}
      </div>
    </form>
  );
}

// ── Help feedback (login + in-app form) ───────────────────────────────────

const HELP_CATEGORY_LABEL = {
  login_issues: 'Log in issues',
  technical_support: 'Technical / IT support',
  suggest_clinical_content: 'Suggest clinical content',
  tool_features_enhancements: 'Tool features / enhancements',
};

const HELP_FREQUENCY_LABEL = {
  multiple_times_per_day: 'Multiple times a day',
  few_times_per_week: 'A few times per week',
  every_so_often: 'Every so often',
  rarely: 'Rarely',
};

const HELP_STATUS_OPTIONS = [
  { value: 'open',           label: 'Open / New' },
  { value: 'triage_thcc_it', label: 'Triage to THCC IT' },
  { value: 'triage_aia',     label: 'Triage to AIA' },
  { value: 'triage_cmo',     label: 'Triage to CMO' },
  { value: 'resolved',       label: 'Resolved / Closed' },
];

// The list defaults to "Active" — open + the triage destinations. Setting a
// feedback to resolved removes it from this view but keeps it in the DB (the
// full history is available via the CSV export).
const HELP_LIST_FILTER_OPTIONS = [
  { value: 'active',         label: 'Active (Open + Triaging)' },
  { value: 'open',           label: 'Open / New' },
  { value: 'triage_thcc_it', label: 'Triage to THCC IT' },
  { value: 'triage_aia',     label: 'Triage to AIA' },
  { value: 'triage_cmo',     label: 'Triage to CMO' },
  { value: 'archived',       label: 'Archived (Resolved / Closed)' },
  { value: 'resolved',       label: 'Resolved / Closed' },
  { value: 'all',            label: 'All statuses' },
];

const HELP_ACTIVE_STATUSES = new Set(['open', 'triage_thcc_it', 'triage_aia', 'triage_cmo']);

function HelpFeedbackRow({ item, onStatusChange }) {
  const [updating, setUpdating] = useState(false);
  const [status, setStatus] = useState(item.status);
  const [comment, setComment] = useState(item.admin_comment || '');
  const [savedComment, setSavedComment] = useState(item.admin_comment || '');
  const [commentSaved, setCommentSaved] = useState(false);
  const [error, setError] = useState('');

  const handleChange = async (e) => {
    const next = e.target.value;
    setError('');
    setUpdating(true);
    try {
      const updated = await adminHelpFeedbackAPI.updateStatus(item.id, { status: next });
      setStatus(updated.status);
      onStatusChange && onStatusChange(updated);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Update failed.';
      setError(typeof detail === 'string' ? detail : 'Update failed.');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveComment = async () => {
    setError('');
    setUpdating(true);
    try {
      const updated = await adminHelpFeedbackAPI.updateStatus(item.id, { comment });
      const saved = updated.admin_comment || '';
      setSavedComment(saved);
      setComment(saved);
      setCommentSaved(true);
      setTimeout(() => setCommentSaved(false), 2000);
      onStatusChange && onStatusChange(updated);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Update failed.';
      setError(typeof detail === 'string' ? detail : 'Update failed.');
    } finally {
      setUpdating(false);
    }
  };

  const commentDirty = comment.trim() !== savedComment.trim();

  const created = new Date(item.created_at).toLocaleString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className={`help-feedback-row help-status-${status}`}>
      <div className="help-feedback-row__top">
        <div className="help-feedback-row__email">
          <span className="help-feedback-row__email-label">From</span>
          <span className="help-feedback-row__email-value">{item.submitter_email}</span>
          {item.user_id && <span className="help-feedback-row__badge">Logged in</span>}
          {item.details_had_pii && <span className="help-feedback-row__badge help-feedback-row__badge--warn">PII masked</span>}
        </div>
        <div className="help-feedback-row__meta">
          <span>{created}</span>
        </div>
      </div>

      <div className="help-feedback-row__chips">
        <span className="help-chip help-chip--category">
          {HELP_CATEGORY_LABEL[item.category] || item.category}
        </span>
        <span className="help-chip help-chip--frequency">
          {HELP_FREQUENCY_LABEL[item.frequency] || item.frequency}
        </span>
      </div>

      <div className="help-feedback-row__details">{item.details_text}</div>

      {item.image_signed_url && (
        <a
          className="help-feedback-row__image-link"
          href={item.image_signed_url}
          target="_blank"
          rel="noopener noreferrer"
          title={item.image_original_name || 'Attached image'}
        >
          <img
            src={item.image_signed_url}
            alt={item.image_original_name || 'attached'}
            className="help-feedback-row__image"
            loading="lazy"
          />
        </a>
      )}

      <div className="help-feedback-row__footer">
        <label className="help-feedback-row__status">
          <span>Status</span>
          <select value={status} onChange={handleChange} disabled={updating}>
            {HELP_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        {item.status_updated_by_username && (
          <span className="help-feedback-row__triager" title={item.status_updated_at ? new Date(item.status_updated_at).toLocaleString() : ''}>
            set by <strong>{item.status_updated_by_username}</strong>
          </span>
        )}
        {error && <span className="help-feedback-row__error">{error}</span>}
      </div>

      <div className="help-feedback-row__comment">
        <label className="help-feedback-row__comment-label" htmlFor={`help-comment-${item.id}`}>
          Comment
        </label>
        <textarea
          id={`help-comment-${item.id}`}
          className="help-feedback-row__comment-input"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a triage note (visible to admins only)…"
          rows={2}
          maxLength={4000}
          disabled={updating}
        />
        <div className="help-feedback-row__comment-actions">
          <button
            type="button"
            className="help-feedback-row__comment-save"
            onClick={handleSaveComment}
            disabled={updating || !commentDirty}
          >
            {updating ? 'Saving…' : 'Save comment'}
          </button>
          {commentSaved && <span className="help-feedback-row__comment-saved">Saved ✓</span>}
        </div>
      </div>
    </div>
  );
}

function HelpFeedbackSection({ onUnreadCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Default to "Active" — open + triage destinations only. Resolved rows drop
  // out of this view but remain in the DB; they're still reachable via the
  // filter dropdown or the CSV export.
  const [statusFilter, setStatusFilter] = useState('active');

  const refreshUnreadCount = async () => {
    if (!onUnreadCountChange) return;
    try {
      const { count } = await adminHelpFeedbackAPI.unreadCount();
      onUnreadCountChange(count);
    } catch { /* badge stays at its last value */ }
  };

  const load = async () => {
    setError('');
    setLoading(true);
    try {
      const rows = await adminHelpFeedbackAPI.list({ status: statusFilter });
      setItems(rows);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Failed to load feedbacks.';
      setError(typeof detail === 'string' ? detail : 'Failed to load feedbacks.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    refreshUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Decide whether an updated row still belongs in the *current* view. When
  // the admin transitions to resolved while on "Active", the row drops
  // out of the list (kept in DB, surfaced in the CSV).
  const stillVisibleUnderFilter = (row) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return HELP_ACTIVE_STATUSES.has(row.status);
    if (statusFilter === 'archived') return !HELP_ACTIVE_STATUSES.has(row.status);
    return row.status === statusFilter;
  };

  const handleStatusChange = (updated) => {
    setItems((prev) => {
      if (!stillVisibleUnderFilter(updated)) {
        return prev.filter((p) => p.id !== updated.id);
      }
      return prev.map((p) => (p.id === updated.id ? updated : p));
    });
    refreshUnreadCount();
  };

  return (
    <div className="help-feedback-section">
      <div className="help-feedback-toolbar">
        <label className="help-feedback-toolbar__filter">
          <span>Filter</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {HELP_LIST_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <button type="button" className="help-feedback-toolbar__refresh" onClick={load}>
          Refresh
        </button>
        <span className="help-feedback-toolbar__hint">
          Resolved / Closed rows are kept in the database — export the CSV report to see the full history.
        </span>
      </div>

      {loading && <div className="help-feedback-empty">Loading…</div>}
      {!loading && error && <div className="help-feedback-error">{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className="help-feedback-empty">
          {statusFilter === 'active'
            ? 'No active feedback to triage. New submissions will appear here.'
            : 'No feedback in this view.'}
        </div>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="help-feedback-list">
          {items.map((item) => (
            <HelpFeedbackRow key={item.id} item={item} onStatusChange={handleStatusChange} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main panel ──────────────────────────────────────────────────────────────

export default function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [unreadHelpCount, setUnreadHelpCount] = useState(0);
  const [adminGuideLoading, setAdminGuideLoading] = useState(false);
  const [adminGuideError, setAdminGuideError] = useState('');

  const handleOpenAdminGuide = async () => {
    setAdminGuideError('');
    setAdminGuideLoading(true);
    try {
      const url = await adminHelpFeedbackAPI.getAdminManualUrl();
      if (!url) {
        setAdminGuideError('Admin guide is unavailable.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const status = err?.response?.status;
      const raw = err?.response?.data?.detail;
      let message;
      if (typeof raw === 'string') {
        message = raw;
      } else if (Array.isArray(raw) && raw[0]?.msg) {
        message = `${raw[0].msg} (${raw[0].loc?.join('.') || 'unknown'})`;
      } else {
        message = err.message || 'Failed to open admin guide.';
      }
      setAdminGuideError(status ? `[${status}] ${message}` : message);
    } finally {
      setAdminGuideLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await authAPI.getCurrentUser();
        if (!mounted) return;
        if (!me?.is_admin) {
          setDenied(true);
        } else {
          setUser(me);
        }
      } catch {
        // Full replace, not a router hop — see the same call in Chat.js.
        if (mounted) endSession();
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  if (loading) return null;

  if (denied) {
    return (
      <div className="admin-shell">
        <div className="admin-denied">
          <h2>Access denied</h2>
          <p>This page is for administrators only.</p>
          <button className="admin-back-btn" onClick={() => navigate('/chat')}>
            Back to PRISM
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <div className="admin-header-inner">
          <button
            type="button"
            className="admin-back-link"
            onClick={() => navigate('/chat')}
          >
            ← Back to PRISM
          </button>
          <div className="admin-title-block">
            <h1 className="admin-title">Admin Panel</h1>
          </div>
          <button
            type="button"
            className="admin-guide-link"
            onClick={handleOpenAdminGuide}
            disabled={adminGuideLoading}
            title={adminGuideError || 'Download the Admin User Guide (PDF)'}
          >
            📘 {adminGuideLoading ? 'Opening…' : 'Admin Guide (PDF)'}
          </button>
          {unreadHelpCount > 0 && (
            <a
              className="admin-help-badge"
              href="#help-feedback"
              title="Jump to Help feedback"
            >
              <span className="admin-help-badge__dot" />
              {unreadHelpCount} new feedback{unreadHelpCount === 1 ? '' : 's'}
            </a>
          )}
        </div>
        {adminGuideError && (
          <div className="admin-header-error">{adminGuideError}</div>
        )}
      </header>

      <main className="admin-main">
        <section className="admin-section">
          <h2 className="admin-section-title">Reports</h2>
          <p className="admin-intro">
            Choose a report, pick a date range, and download a CSV. Dates
            before the first available record are trimmed automatically, and a
            range with no matching data downloads an empty CSV. Every export is
            recorded in the audit log.
          </p>
          <div className="report-grid">
            <QAReportCard />
            <FeedbackReportCard />
            <LoginsReportCard />
            <CertificatesReportCard />
            <HelpFeedbackReportCard />
          </div>
        </section>

        <section className="admin-section" id="help-feedback">
          <h2 className="admin-section-title">Help Feedback</h2>
          <p className="admin-intro">
            User-submitted feedback from the in-app and login-page Help form.
            Update the status as you triage each one — every status change is
            recorded in the audit log.
          </p>
          <HelpFeedbackSection onUnreadCountChange={setUnreadHelpCount} />
        </section>

        <section className="admin-section">
          <h2 className="admin-section-title">Update the Knowledge Base</h2>
          <p className="admin-intro">
            Upload a PDF to the production knowledge base.
          </p>
          <KBSection />
        </section>

        <section className="admin-section">
          <h2 className="admin-section-title">Update User Manual</h2>
          <p className="admin-intro">
            Replace the PRISM User Manual PDF for English or French. The new
            file overwrites the current copy in storage and is served to users
            immediately the next time they open the Help modal.
          </p>
          <UserManualUploadCard />
        </section>

        <section className="admin-section">
          <h2 className="admin-section-title">Update Admin Manual</h2>
          <p className="admin-intro">
            Replace the PRISM Admin Manual PDF (English only). The new file
            overwrites the current copy in storage and is available immediately
            from the Admin Guide link in the page header.
          </p>
          <AdminManualUploadCard />
        </section>
      </main>
    </div>
  );
}
